/* eslint-disable react/jsx-max-depth */
import { useQuery } from '@apollo/client';
import { IonText } from '@ionic/react';
import { useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import TertiaryButton from '@components/buttons/TertiaryButton';
import DateStamp from '@components/dates/DateStamp';
import styles from '@components/expert/ExpertPackageGrantPage.module.css';
import PackageGrantEligibilityDecisionForm from '@components/forms/PackageGrantEligibilityDecisionForm';
import HandleQuery from '@components/HandleQuery';
import LargeLabel from '@components/labels/LargeLabel';
import AppPage from '@components/pageTypes/AppPage';
import useSubscribeToAllPackageGrants from '@hooks/subscriptions/useSubscribeToAllPackageGrants';
import {
  GetPackageGrantDocument,
  GetPackageGrantQuery,
  GetPackageGrantQueryVariables,
  PackageGrantEligibilityStatusEnum
} from '@typing/Generated';

type FieldItemProps = {
  date?: Date;
  labelId: string;
  value?: string | null;
};

const FieldItem = ({ date, labelId, value }: FieldItemProps) => (
  <div>
    <div className={styles.label}>
      <LargeLabel>
        <FormattedMessage id={labelId} />
      </LargeLabel>
    </div>
    <div className={styles.value}>{date ? <DateStamp value={date} /> : <IonText>{value}</IonText>}</div>
  </div>
);

type Props = {
  packageGrantId: string;
};

const ExpertPackageGrantPage = ({ packageGrantId }: Props) => {
  const intl = useIntl();
  const history = useHistory();
  const contentRef = useRef<HTMLIonContentElement>(null);

  const getPackageGrantQuery = useQuery<GetPackageGrantQuery, GetPackageGrantQueryVariables>(GetPackageGrantDocument, {
    variables: { id: packageGrantId }
  });

  useSubscribeToAllPackageGrants(packageGrant => {
    if (packageGrant?.id === packageGrantId) {
      getPackageGrantQuery.refetch();
    }
  });

  const handlePackageGrantError = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollToTop(2000);
    }
  }, [contentRef]);

  const backToDashboard = useCallback(() => {
    history.goBack();
  }, [history]);

  return (
    <AppPage>
      <Helmet>
        <title>{intl.formatMessage({ id: 'pageTitles.expert.packageGrant.show' })}</title>
      </Helmet>
      <HandleQuery query={getPackageGrantQuery}>
        {data => (
          <div className={styles.page}>
            <TertiaryButton
              icon="chevronLeft"
              onClick={backToDashboard}
              textKey="pages.expert.packageGrant.dashboard"
            />
            {data.packageGrant.eligibilityStatus === PackageGrantEligibilityStatusEnum.NEEDS_REVIEW && (
              <>
                <div className={styles.review}>
                  <FormattedMessage
                    id="pages.expert.packageGrant.review"
                    values={{ name: data.packageGrant.client.fullName }}
                  />
                </div>
                <div className={styles.purpleText}>
                  <FormattedMessage
                    id="pages.expert.packageGrant.eligibilityDate"
                    values={{ date: <DateStamp value={data.packageGrant.createdAt} /> }}
                  />
                </div>
                <FieldItem labelId="pages.expert.packageGrant.name" value={data.packageGrant.client.fullName} />
                <FieldItem
                  labelId="pages.expert.packageGrant.enteredEmployer"
                  value={data.packageGrant.client.enteredEmployer}
                />
                <FieldItem
                  labelId="pages.expert.packageGrant.personalEmail"
                  value={data.packageGrant.client.email.email}
                />
                <FieldItem
                  labelId="pages.expert.packageGrant.workEmail"
                  value={data.packageGrant.client.workEmail?.email}
                />
                <FieldItem date={data.packageGrant.client.createdAt} labelId="pages.expert.packageGrant.createdAt" />

                <PackageGrantEligibilityDecisionForm
                  onError={handlePackageGrantError}
                  onSuccess={backToDashboard}
                  packageGrant={data.packageGrant}
                />
              </>
            )}
          </div>
        )}
      </HandleQuery>
    </AppPage>
  );
};

export default ExpertPackageGrantPage;
